import { get } from "lodash-es";
import { match, P } from "ts-pattern";

import { authService } from "~/modules/Auth/authService";
import { useAuthStore } from "~/modules/Auth/authStore";

import { API } from "./generated-api";

export const requestManager = {
  controller: new AbortController(),

  abortAll() {
    this.controller.abort();
    this.controller = new AbortController();
  },
};

const baseURL = (() => {
  const importEnvMode = get(import.meta.env, "MODE") || undefined;
  const windowEnvApiUrl =
    get(typeof window !== "undefined" ? window : {}, "ENV.VITE_API_URL") || undefined;
  const importEnvApiUrl = import.meta.env.VITE_API_URL || undefined;
  const processEnvApiUrl =
    get(typeof process !== "undefined" ? process.env : {}, "VITE_API_URL") || undefined;

  const resolvedApiUrl = importEnvApiUrl || windowEnvApiUrl || processEnvApiUrl;

  return match({
    importEnvMode,
    resolvedApiUrl,
  })
    .with({ importEnvMode: "test" }, () => "http://localhost:3000")
    .with({ resolvedApiUrl: P.string }, () => resolvedApiUrl)
    .otherwise(() => undefined);
})();

export const ApiClient = new API({
  baseURL,
  secure: true,
  withCredentials: true,
});

ApiClient.instance.interceptors.request.use((config) => {
  const isPublicOrAuthEndpoint =
    config.url?.includes("/login") ||
    config.url?.includes("/refresh") ||
    config.url?.includes("/forgot-password") ||
    config.url?.includes("/register") ||
    config.url?.includes("/send-otp") ||
    config.url?.includes("/verify-otp") ||
    config.url?.includes("/current-user") ||
    config.url?.includes("/settings/") ||
    config.url?.includes("/env/") ||
    config.url?.includes("/sso");

  if (!isPublicOrAuthEndpoint && !useAuthStore.getState().isLoggedIn) {
    config.signal = requestManager.controller.signal;
  }

  return config;
});

const FORBIDDEN = 403;
const UNAUTHORIZED = 401;

/**
 * Regla única de este interceptor: **401 = "no sé quién sos" · 403 = "sé quién sos y esto no es
 * tuyo"**. Sólo el primero puede tocar la sesión.
 *
 * Por qué importa: todo el front deriva `isLoggedIn` de `currentUser`, así que cualquier cosa que
 * llame a `authService.logout()` desloguea la app por dentro. Cuando las puertas de autorización de
 * lecciones respondían 401 (clase bloqueada por secuencia, curso no comprado), abrir una clase
 * todavía bloqueada expulsaba al alumno. Los códigos del servidor ya se corrigieron a 403 con `code`
 * estable (`AUTHORIZATION_ERROR_CODES` en `@repo/shared`); acá se cierra la otra punta.
 */
ApiClient.instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Un /logout que falla no se reintenta ni refresca: ya estamos cerrando la sesión.
    //
    // `/refresh` va en la misma lista, y es lo que evita una recursión: `authService.refreshToken()`
    // pega con ESTE MISMO cliente, así que si el propio `/auth/refresh` contesta 401 vuelve a
    // entrar acá. Y entra con un `config` NUEVO —`_retry` no viaja entre requests— con
    // `isLoggedIn` todavía en true, o sea que cumple las tres condiciones de abajo y llama a
    // `refreshToken()` de nuevo. La app deja de renovar la sesión y encima se martilla a sí misma
    // hasta que revienta el stack.
    //
    // Excluyéndolo, un refresh que falla cae derecho al `catch` de abajo, que es donde el logout
    // tiene que pasar: el token está muerto de verdad.
    if (error.config?.url?.includes("/logout") || error.config?.url?.includes("/refresh")) {
      return Promise.reject(error);
    }

    // 403 EXPLÍCITO: se rechaza tal cual, sin refresh y sin tocar el store. La pantalla que pidió el
    // dato es la que tiene que mostrar "no tenés acceso" (y con `code` sabe exactamente por qué).
    // Estaba implícito en el `=== 401` de abajo; queda escrito para que no se pierda en el próximo
    // refactor, porque el costo de perderlo es expulsar al alumno de la plataforma.
    if (error.response?.status === FORBIDDEN) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === UNAUTHORIZED &&
      !error.config._retry &&
      useAuthStore.getState().isLoggedIn
    ) {
      error.config._retry = true;
      try {
        // OJO con el orden: `logout()` va en el catch, NO acá. Llamarlo antes del refresh borraba
        // `currentUser` incluso cuando el refresh funcionaba, y nadie volvía a poner
        // `isLoggedIn = true`: la app quedaba "medio deslogueada" (el siguiente 401 ya no
        // refrescaba porque la condición exige `isLoggedIn`, y el request interceptor empezaba a
        // colgarle el AbortController compartido a todos los pedidos). Un token vencido que se
        // renueva bien tiene que ser invisible para el usuario.
        await authService.refreshToken();
        // SIN `await` a propósito: el reintento se devuelve como promesa para que su propio fallo NO
        // caiga en este catch. Si el reintento vuelve a dar 401, el token no era el problema
        // (`_retry` ya está marcado, así que no hay loop) y desloguear sería castigar al usuario por
        // un rechazo puntual del endpoint.
        return ApiClient.instance(error.config);
      } catch {
        // Recién acá se cae la sesión: el refresh falló, el token está muerto de verdad.
        authService.logout();
        requestManager.abortAll();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);
